import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useBackTarget } from "@/lib/navigation";
import { cn } from "@/lib/utils";

/** Standard page title + description, used at the top of every route. One
 *  place to change the heading style app-wide instead of six copies of the
 *  same h1/p markup. `actions` renders a primary button/control on the
 *  right (e.g. Policies' "Save as new version") - the header wraps to a
 *  second line on narrow screens rather than compressing. `titleActions`
 *  is for something small enough to sit directly beside the title text
 *  itself (e.g. a refresh icon button) - unlike `actions`, it never wraps
 *  to its own line at any width, since it's laid out inline with the `<h1>`
 *  rather than at the far end of the header row.
 *
 *  Every page except the Dashboard gets a back arrow beside its title,
 *  derived from the route (see lib/navigation.ts) - pages don't pass it.
 *  Tablet/desktop only: on phones the same arrow lives in MobileHeader's
 *  top bar instead, so it isn't shown twice. */
export function PageHeader({
  title,
  description,
  actions,
  titleActions
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  titleActions?: React.ReactNode;
}) {
  const back = useBackTarget();
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        {back && (
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="mt-0.5 hidden h-8 w-8 shrink-0 md:inline-flex"
          >
            <Link to={back.to} title={`Back to ${back.label}`}>
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to {back.label}</span>
            </Link>
          </Button>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold tracking-tight sm:text-xl">
              {title}
            </h1>
            {titleActions}
          </div>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      {actions}
    </div>
  );
}

/** Standard page padding/spacing wrapper - the outer <div> every page
 *  starts with. Centralizes the responsive padding scale (p-4 on phones up
 *  to p-8 on desktop) so it changes in one place, and caps line length on
 *  very wide monitors so a form page doesn't stretch edge to edge.
 *
 *  `fullWidth` drops that cap - for a page whose content IS a wide table
 *  (My Reimbursements, My Approvals, Approver Queue, User Management, Audit
 *  Logs), the capped width just left symmetric dead margins on a large
 *  monitor with nothing useful filling them, unlike a form (the request
 *  wizard, Policies' own flows) where the cap keeps lines a comfortable
 *  reading length - those keep the cap, unaffected by this prop existing. */
export function PageContainer({
  children,
  fullWidth = false
}: {
  children: React.ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div
      className={cn(
        "mx-auto w-full space-y-5 p-4 sm:space-y-6 sm:p-6 lg:p-6 lg:pr-1",
        fullWidth ? "max-w-none" : "max-w-screen-2xl"
      )}
    >
      {children}
    </div>
  );
}
